import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { UserRound } from 'lucide-react'
import { useLanguage } from '../hooks/useLanguage'
import { NotificationBell } from './NotificationBell'
import { DateCalendarPicker } from './DateCalendarPicker'

interface HeaderProps {
  dates: string[]
  selectedDate?: string
  selectedNarrative?: string
  onDateChange: (date: string) => void
  isLoading?: boolean
}

function CalendarIcon() {
  return <span className="text-sm leading-none" aria-hidden="true">📅</span>
}

export function Header({
  dates,
  selectedDate,
  onDateChange,
  isLoading,
}: HeaderProps) {
  const navigate = useNavigate()
  const { lang, setLang, t } = useLanguage()
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const user = WebApp.initDataUnsafe?.user

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDatePickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-20">
      <div className="max-w-3xl mx-auto px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold text-gray-950 dark:text-white leading-none tracking-normal">
              Web3 Events
            </h1>
            <p className="mt-1.5 text-base text-gray-500 dark:text-gray-400 leading-tight truncate">
              Daily Web3 Narrative Monitor
            </p>
          </div>

          <div className="flex items-start gap-2 shrink-0">
            <NotificationBell />
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                onClick={() => navigate('/me')}
                className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:bg-gray-800 dark:text-gray-200"
                aria-label={t('我的', 'Me')}
              >
                {user?.photo_url
                  ? <img src={user.photo_url} alt="" className="h-full w-full object-cover" />
                  : <UserRound className="h-[18px] w-[18px]" />}
              </button>
              <div
                className="flex rounded-full border border-gray-200 bg-gray-50 p-0.5 text-[10px] font-semibold leading-none dark:border-gray-700 dark:bg-gray-800"
                aria-label={t('切换语言', 'Switch language')}
              >
                <button
                  type="button"
                  onClick={() => setLang('zh')}
                  className={`rounded-full px-1.5 py-1 transition-colors ${lang === 'zh' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                  aria-pressed={lang === 'zh'}
                >
                  中
                </button>
                <button
                  type="button"
                  onClick={() => setLang('en')}
                  className={`rounded-full px-1.5 py-1 transition-colors ${lang === 'en' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                  aria-pressed={lang === 'en'}
                >
                  EN
                </button>
              </div>
            </div>

            {isLoading && (
              <svg className="animate-spin w-4 h-4 text-blue-500 shrink-0" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-gray-950 dark:text-white">
          <div className="relative shrink-0" ref={dropdownRef}>
            <button
              type="button"
              data-analytics-id="date-picker-toggle"
              onClick={() => setIsDatePickerOpen((open) => !open)}
              disabled={isLoading || dates.length === 0}
              className="inline-flex items-center gap-1 min-h-[28px] text-sm font-medium text-gray-900 dark:text-white disabled:opacity-50 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/30 rounded"
            >
              <CalendarIcon />
              <span>{selectedDate || t('选择日期', 'Select Date')}</span>
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isDatePickerOpen && dates.length > 0 && (
              <div className="absolute left-0 top-full z-30 mt-2">
                <DateCalendarPicker
                  dates={dates}
                  selectedDate={selectedDate}
                  onSelect={(date) => {
                    onDateChange(date)
                    setIsDatePickerOpen(false)
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
